import type { GraphCategory, GraphFilters } from '../../services/graphApi';

export type StatisticsFilterState = {
  gender: string;
  categoryId: string;
  ageGroup: string;
  group: string;
};

type StatisticsFilterBarProps = Readonly<{
  filters: StatisticsFilterState;
  categories: GraphCategory[];
  genderOptions: string[];
  ageGroupOptions: string[];
  className?: string;
  disabled?: boolean;
  showAgeGroupFilter?: boolean;
  collectBirth?: boolean;
  collectGender?: boolean;
  onChange: (filters: StatisticsFilterState) => void;
  onReset: () => void;
}>;

export const emptyStatisticsFilters: StatisticsFilterState = {
  gender: '',
  categoryId: '',
  ageGroup: '',
  group: '',
};

export function toGraphFilters(filters: StatisticsFilterState): GraphFilters {
  return {
    gender: filters.gender,
    categoryId: filters.categoryId,
    ageGroup: filters.ageGroup,
    group: filters.group,
  };
}

export function uniqueFilterValues(values: Array<string | null | undefined>) {
  return Array.from(new Set(values.filter((value): value is string => Boolean(value)))).sort(
    (firstValue, secondValue) => firstValue.localeCompare(secondValue, 'ko')
  );
}

type SelectOption = Readonly<{
  key: string;
  label: string;
  value: string;
}>;

type FilterSelectProps = Readonly<{
  label: string;
  value: string;
  placeholder: string;
  options: SelectOption[];
  disabled?: boolean;
  onChange: (value: string) => void;
}>;

function FilterSelect({
  label,
  value,
  placeholder,
  options,
  disabled,
  onChange,
}: FilterSelectProps) {
  return (
    <select
      aria-label={label}
      className="split-filter-control"
      value={value}
      disabled={disabled}
      onChange={(event) => onChange(event.target.value)}
    >
      <option value="">{placeholder}</option>
      {options.map((option) => (
        <option value={option.value} key={option.key}>
          {option.label}
        </option>
      ))}
    </select>
  );
}

function toTextOptions(options: string[]): SelectOption[] {
  return options.map((option) => ({
    key: option,
    label: option,
    value: option,
  }));
}

function toCategoryOptions(categories: GraphCategory[]): SelectOption[] {
  return categories.map((category) => ({
    key: String(category.id),
    label: category.categoryName,
    value: String(category.id),
  }));
}

function toGroupOptions(groups: NonNullable<GraphCategory['groups']>): SelectOption[] {
  return groups.map((group) => ({
    key: `${group.groupId}-${group.groupName}`,
    label: group.groupName,
    value: group.groupName,
  }));
}

function StatisticsFilterBar({
  filters,
  categories,
  genderOptions,
  ageGroupOptions,
  className = '',
  disabled,
  showAgeGroupFilter = true,
  collectBirth = true,
  collectGender = true,
  onChange,
  onReset,
}: StatisticsFilterBarProps) {
  const selectedCategory = categories.find((category) => String(category.id) === filters.categoryId);
  const groupOptions = selectedCategory?.groups ?? [];
  const formClassName = className ? `statistics-filter-row ${className}` : 'statistics-filter-row';

  const updateFilter = (key: keyof StatisticsFilterState, value: string) => {
    onChange({
      ...filters,
      [key]: value,
      ...(key === 'categoryId' ? { group: '' } : {}),
    });
  };

  return (
    <form className={formClassName} onSubmit={(event) => event.preventDefault()}>
      <FilterSelect
        label="종목"
        value={filters.categoryId}
        placeholder="종목 전체"
        options={toCategoryOptions(categories)}
        disabled={disabled}
        onChange={(value) => updateFilter('categoryId', value)}
      />
      <FilterSelect
        label="그룹"
        value={filters.group}
        placeholder="그룹"
        options={toGroupOptions(groupOptions)}
        disabled={disabled || !selectedCategory}
        onChange={(value) => updateFilter('group', value)}
      />
      {showAgeGroupFilter && (
        <FilterSelect
          label="나이그룹"
          value={collectBirth ? filters.ageGroup : ''}
          placeholder="나이그룹 전체"
          options={toTextOptions(ageGroupOptions)}
          disabled={disabled || !collectBirth || ageGroupOptions.length === 0}
          onChange={(value) => updateFilter('ageGroup', value)}
        />
      )}
      <FilterSelect
        label="성별"
        value={collectGender ? filters.gender : ''}
        placeholder="성별 전체"
        options={toTextOptions(genderOptions)}
        disabled={disabled || !collectGender || genderOptions.length === 0}
        onChange={(value) => updateFilter('gender', value)}
      />
      <button className="split-reset-button" type="button" disabled={disabled} onClick={onReset}>
        초기화
      </button>
    </form>
  );
}

export default StatisticsFilterBar;
